// @ts-check
import {
  onTabletToDesktop, isMobile, getValueByNs, delegateEvents
} from '../../utils/index.js';
import ResizeObserver from '../../resize-observer-polyfill.js';
import { createElement } from '../../helpers/dom-elements.js';
import * as modal from '../modal.js';
import * as modalInterface from '../modal-interface.js';
import { getDisplayedProductsQuantity, findProductsInCartById } from './service.js';
import { createPesIcon } from '../../../utils/utils';

export {
  getZipCodeInput, getSearchForm, clearDistributorsList, showLoader, renderNoResults, showZipCodeAlertMessage,
  showRedirectWarning, showValidationMessage, hideValidationMessage
} from '../modal-interface.js';

/** @typedef {import('./cart-transfer').CartTransferOptions} CartTransferOptions */
/** @typedef {import('./cart-transfer').CartTransferLabels} CartTransferLabels */
/** @typedef {import('./cart-transfer').CartTransferMessages} CartTransferMessages */

const MAX_UNAVAILABLE_ITEMS_TO_SHOW = 3;
const MAX_PRODUCTS_BEFORE_HIDING = 5;

const elements = {
  /** @type {HTMLStyleElement} */
  style: null,
  /** @type {HTMLDivElement} */
  infoBlock: null
};

let resizeObserver;

function addInlineStyle() {
  if (!elements.style) {
    elements.style = document.createElement('style');
    document.body.appendChild(elements.style);
  }

  // compensation of a negative simple bar bottom padding
  const correction = 34;
  const listMaxHeight = modal.maxContentHeight() + correction;
  const scrollbarWidth = modal.getScrollbarWidth();
  let scrollContentStyles = '';

  if (scrollbarWidth) {
    scrollContentStyles += `.cc-modal.cc-modal--cart .cc-modal__content .simplebar-scroll-content > .simplebar-content {
      width: calc(100% + ${scrollbarWidth}px);
    }`;
  }

  elements.style.textContent = `@media screen and (min-width: 981px){
      .cc-distributors-list {
        max-height: ${listMaxHeight}px;
      }
    }
    ${scrollContentStyles}`;
}

onTabletToDesktop(() => {
  if (modal.isOpened()) {
    addInlineStyle();
  }
});

/**
 * Build info section (includes zip code input field).
 *
 * @param {ModalParams} params
 * @returns {HTMLElement}
 */
function renderModalInfoBlock({ labels, messages }) {
  const searchForm = modalInterface.renderSearchForm({
    labels: {
      title: labels.ct_labels_search_title,
      placeholder: labels.ct_labels_search_placeholder,
      button: labels.ct_labels_search_button,
      invalidZipCodeError: messages.ct_messages_invalid_zip_code_error
    }
  });

  elements.infoBlock = createElement('div', { className: 'cc-info-block cc-show-search', contents: searchForm });

  return elements.infoBlock;
}

/**
 * @typedef {object} ModalParams
 * @property {CartTransferOptions} options
 * @property {CartTransferLabels} labels
 * @property {CartTransferMessages} messages
 */

/**
 * Open Cart Transfer modal.
 *
 * @param {ModalParams} params
 */
export function openModal(params) {
  if (modal.isOpened()) {
    resizeObserver.disconnect();
    modal.close();
  }

  const bodyExtraChildren = [];

  if (params.options.useZipCode) {
    bodyExtraChildren.push(renderModalInfoBlock(params));
  }

  const modalBody = modalInterface.renderModalBody(bodyExtraChildren);
  const modalWindow = modal.open(modalBody, params.labels.ct_labels_modal_header, { classList: 'cc-modal--cart' });

  addInlineStyle();

  resizeObserver = new ResizeObserver(addInlineStyle);

  resizeObserver.observe(modalWindow);
}

// TODO: Avoid keeping these. Instead pass to methods during a call
let _productsInCart;
let _unavailableProductsObject;
const _products = {};
/** @type {CartTransferOptions} */
let _options;
/** @type {CartTransferLabels} */
let _labels;
/** @type {CartTransferMessages} */
let _messages;

/**
 * @param {object} product
 * @param {boolean} [isLink]
 * @returns {HTMLElement}
 */
function createProductItem(product, isLink) {
  const productItemImg = createElement('img', {
    src: product.imgSrc,
    alt: product.imgAlt,
    title: product.commRef,
    className: 'product-item__img'
  });

  const productItemTitle = createElement('h4', {
    className: 'product-item__title',
    innerText: product.commRef
  });

  const productItemDescription = createElement('div', {
    className: 'product-item__description',
    innerText: product.commDesc
  });

  return createElement(isLink ? 'a' : 'span', {
    className: 'product-item',
    ...(isLink && { href: product.href }),
    contents: [
      productItemImg,
      productItemTitle,
      productItemDescription
    ]
  });
}

/**
 * Creates product title element.
 *
 * @param {object} [product] Product object.
 * @param {string} product.href Product link.
 * @param {string} product.commRef Product id.
 * @returns {HTMLElement | Text} Product title element status.
 */
function createProductTitle(product) {
  if (!product) {
    return document.createTextNode('');
  }

  return createElement('a', {
    className: 'product-list-item__link',
    href: product.href,
    contents: createElement('span', {
      className: 'product-list-item__link-text',
      innerText: product.commRef,
      contents: createProductItem(product)
    })
  });
}

/**
 * Get product stock status text value.
 *
 * @param {string} status Product stock status.
 * @returns {string} Product stock status text.
 */
function getProductStockStatusTextValue(status) {
  // TODO:CartTransfer use named statuses:
  // https://bitbucket.org/schneideripo/pes-v2-atg/pull-requests/4829/pesv3-10309-fixed-zip-code-parsing/diff#comment-91226349
  const stockStatusMatrix = {
    '-1': 'not-distributed',
    0: 'out-of-stock',
    1: 'not-all-available',
    2: 'all-available'
  };

  return stockStatusMatrix.hasOwnProperty(status) ? stockStatusMatrix[status] : 'unknown status';
}

/**
 * Create product status element.
 *
 * @param {string} status Product stock status.
 * @param {string} [retailerName] Product retailer name.
 * @returns {HTMLElement} Product status element.
 */
function createProductStatus(status, retailerName) {
  let stockAvailabilityStatusText = '';

  switch (status) {
    case '-1':
      stockAvailabilityStatusText = _labels.ct_labels_not_distributed.replace(/#distributor name#/, retailerName);
      break;
    case '0':
      stockAvailabilityStatusText = _labels.ct_labels_out_of_stock;
      break;
    default:
      stockAvailabilityStatusText = _labels.ct_labels_all_available_in_stock;
  }

  return createElement('span', {
    classList: [
      'product-list-item__stock-availability-status',
      `product-list-item__stock-availability-status--${getProductStockStatusTextValue(status)}`
    ],
    innerText: stockAvailabilityStatusText
  });
}

/**
 * Get product stock status
 * TODO:CartTransfer Use when channelsight will fix its response data
 * https://bitbucket.org/schneideripo/pes-v2-atg/pull-requests/4829/pesv3-10309-fixed-zip-code-parsing/diff#comment-91226349.
 *
 * @param {object} product Product object.
 * @param {number} [product.StockLevel] Product stock level.
 * @param {number} [product.QuantityRequested] Product requested quantity.
 * @returns {string | number} Product stock status.
 * */
// eslint-disable-next-line no-unused-vars
function getProductStockStatus(product) {
  let stockStatus = '-1';

  if (product.StockLevel >= 0) {
    if (product.StockLevel > 0) {
      stockStatus = (product.StockLevel >= product.QuantityRequested) ? 2 : 1;
    } else {
      stockStatus = 0;
    }
  }

  return stockStatus;
}

/**
 * Create product row element.
 *
 * @param {object} product Product object.
 * @param {number} index Product object index in products array.
 * @param {Array} productsArray Products array.
 * @param {HTMLElement} rowsElement Products rows element.
 * @returns {HTMLElement} Product row element.
 */
function createProductRow(product, index, productsArray, rowsElement) {
  // TODO:CartTransfer use getProductStockStatus function when channelsight will fix its response data
  const stockStatus = rowsElement.dataset.stock_status;
  const stockAvailabilityStatus = createProductStatus(stockStatus, rowsElement.dataset.retailer_name);

  const row = createElement('li', {
    className: 'product-list-item',
    contents: [
      createProductTitle(_products[product.ProductId]),
      stockAvailabilityStatus
    ]
  });

  if (stockStatus === '-1') {
    rowsElement.classList.add('retailer__product-list--not-distributed');

    if (index === productsArray.length - 1) {
      const contactMessage = document.createElement('div');

      contactMessage.className = 'product-list-item__stock-availability-status-contact';

      const contactMessageText = _messages.ct_messages_not_distributed.match(/^(.*)#start link#(.*)#end link#(.*)$/);

      const contactMessageTextBeforeLink = document.createTextNode(getValueByNs(contactMessageText, '1'));
      const contactMessageTextAfterLink = document.createTextNode(getValueByNs(contactMessageText, '3'));
      const contactMessageLinkText = getValueByNs(contactMessageText, '2');

      contactMessage.appendChild(contactMessageTextBeforeLink);

      if (_options.notDistributedCtaURL && contactMessageLinkText) {
        const contactMessageLink = document.createElement('a');

        contactMessageLink.href = _options.notDistributedCtaURL;
        contactMessageLink.innerText = contactMessageLinkText;
        contactMessage.appendChild(contactMessageLink);
      } else {
        contactMessage.appendChild(document.createTextNode(contactMessageLinkText));
      }

      contactMessage.appendChild(contactMessageTextAfterLink);

      stockAvailabilityStatus.appendChild(contactMessage);
    }
  }

  return row;
}

/**
 * Create products rows container element.
 *
 * @param {HTMLElement} list Products rows container element.
 * @param {object} product Product object.
 * @param {number} index Product object index in products array.
 * @param {Array} productsArray Products array.
 * @returns {HTMLElement} Products rows container element.
 */
function appendProductRows(list, product, index, productsArray) {
  const row = createProductRow(product, index, productsArray, list);

  list.appendChild(row);
  return list;
}

/**
 * Create products rows container element.
 *
 * @param {Array} productsArray Products array.
 * @param {string} retailerName Retailer name.
 * @param {string} stockStatus Products status.
 * @returns {HTMLElement | Text} Products rows container element.
 */
function createProductRows(productsArray, retailerName, stockStatus) {
  if (!productsArray || !productsArray.length) {
    return document.createTextNode('');
  }

  const productsWrapper = createElement('ul', {
    className: 'retailer__product-list',
    dataset: {
      retailer_name: retailerName,
      stock_status: stockStatus
    }
  });

  const productIds = productsArray.map((product) => product.ProductId);

  const foundProducts = findProductsInCartById(productIds, _productsInCart);

  if (foundProducts.length) {
    foundProducts.forEach((product) => {
      _products[product.commRef] = { ...product };
    });
  }

  return productsArray.reduce(appendProductRows, productsWrapper);
}

function filterUnavailableOnlyForRetailerProducts(product) {
  return !_unavailableProductsObject.hasOwnProperty(product.ProductId);
}

/**
 * @param {object} retailer
 * @returns {HTMLElement}
 */
function createExpandedDetails(retailer) {
  let filteredNotCarriedProducts = retailer.ProductsNotCarried;

  if (_unavailableProductsObject && getValueByNs(retailer, 'ProductsNotCarried.length', 0)) {
    filteredNotCarriedProducts = retailer.ProductsNotCarried.filter(filterUnavailableOnlyForRetailerProducts);
  }

  const productsInStock = createProductRows(retailer.ProductsCarriedInStock, retailer.DisplayName, '1');
  const productsOutOfStock = createProductRows(retailer.ProductsCarriedOutOfStock, retailer.DisplayName, '0');
  const productsNotCarried = createProductRows(filteredNotCarriedProducts, retailer.DisplayName, '-1');

  return createElement('div', {
    className: 'retailer-item__details-expanded-info',
    contents: [
      productsInStock,
      productsOutOfStock,
      productsNotCarried
    ]
  });
}

/**
 * @param {object} retailer
 * @param {boolean} allInStock
 * @param {number} inStockQuantity
 * @param {number} totalProductsQuantity
 * @returns {HTMLElement}
 */
function createDetails(retailer, allInStock, inStockQuantity, totalProductsQuantity) {
  const retailerDetailsHeader = createElement('div', {
    className: 'retailer-item__details-header'
  });

  const retailerDetailsHeaderInfo = createElement('div', {
    className: 'retailer-item__details-header-info js-view-retailer-item-details'
  });

  if (allInStock) {
    retailerDetailsHeaderInfo.classList.add('retailer-item__details-header-info--in-stock');

    const allInStockIcon = createElement('span', {
      className: 'retailer-item__details-header-info-icon vertical_middle',
      contents: createPesIcon({
        iconName: 'notification_ok',
        size: 'MEDIUM',
        className: 'figure-icon'
      })
    });

    retailerDetailsHeaderInfo.appendChild(allInStockIcon);
  }

  const viewDetailsTogglerMobileIcon = createElement('span', {
    className: 'retailer-item__details-header-info-expand-icon icon figure-icon pes-desktop-hide vertical_middle',
    contents: createPesIcon({
      iconName: 'arrow2_right',
      size: 'NANO',
      className: 'figure-icon'
    })
  });

  const retailerDetailsHeaderInfoText = document.createTextNode(`${_labels.ct_labels_details_title
    .replace(/#number of available products#/, inStockQuantity.toString())
    .replace(/#number of all the requested products#/, totalProductsQuantity.toString())}`);

  retailerDetailsHeaderInfo.appendChild(retailerDetailsHeaderInfoText);
  retailerDetailsHeaderInfo.appendChild(viewDetailsTogglerMobileIcon);

  retailerDetailsHeader.appendChild(retailerDetailsHeaderInfo);

  const displayedProductsQuantity = getDisplayedProductsQuantity(
    retailer,
    totalProductsQuantity,
    _unavailableProductsObject
  );

  const largeProductsQuantity = displayedProductsQuantity > MAX_PRODUCTS_BEFORE_HIDING;

  if (largeProductsQuantity) {
    const viewDetailsToggler = createElement('button', {
      className: 'retailer-item__details-header-expand pes-mobile-hide js-view-retailer-item-details-toggler js-view-retailer-item-details',
      type: 'button',
      innerText: _labels.ct_labels_expand_details,
      contents: createElement('span', {
        className: 'retailer-item__details-header-expand-icon icon vertical_middle',
        contents: createPesIcon({
          iconName: 'arrow2_right',
          size: 'NANO',
          className: 'figure-icon'
        })
      })
    });

    retailerDetailsHeader.appendChild(viewDetailsToggler);
  }

  return createElement('div', {
    className: 'retailer-item__details js-retailer-details',
    contents: [
      retailerDetailsHeader,
      createExpandedDetails(retailer)
    ]
  });
}

/**
 * @param {HTMLElement} child
 * @returns {HTMLElement}
 */
function createRetailerItemColumn(child) {
  return createElement('div', {
    className: 'retailer-item__column',
    contents: child
  });
}

/**
 * Render Retailer row element.
 *
 * @param {object} retailer Retailer object.
 *  @param {string} retailer.RetailerLogo Retailer logo image source.
 *  @param {string} retailer.DisplayName Retailer name to display.
 *  @param {string} retailer.RetailerCartId Retailer cart id.
 *  @param {Array} [retailer.ProductsCarriedInStock] Retailer's array of products in stock.
 *  @param {Array} [retailer.ProductsCarriedOutOfStock] Retailer's array of products out of stock.
 *  @param {Array} [retailer.ProductsNotCarried] Retailer's array of not carried products.
 * @param {object} callbacks
 * @param {(cartId: string, button: EventTarget) => void} callbacks.onCartTransferButtonClick
 * @returns {HTMLElement | Text} Retailer Row element.
 */
export function renderRetailerRow(retailer, { onCartTransferButtonClick }) {
  const imgColumn = createRetailerItemColumn(createElement('img', {
    src: retailer.RetailerLogo,
    alt: retailer.DisplayName,
    title: retailer.DisplayName,
    className: 'retailer-item__img js-view-retailer-item-details'
  }));

  const inStockQuantity = retailer.ProductsCarriedInStock ? retailer.ProductsCarriedInStock.length : 0;
  const outOfStockQuantity = retailer.ProductsCarriedOutOfStock ? retailer.ProductsCarriedOutOfStock.length : 0;
  const notCarriedQuantity = retailer.ProductsNotCarried ? retailer.ProductsNotCarried.length : 0;

  if (!inStockQuantity && !outOfStockQuantity) {
    return document.createTextNode('');
  }

  const totalProductsQuantity = inStockQuantity + outOfStockQuantity + notCarriedQuantity;

  const displayedProductsQuantity = getDisplayedProductsQuantity(
    retailer,
    totalProductsQuantity,
    _unavailableProductsObject
  );

  const largeProductsQuantity = displayedProductsQuantity > MAX_PRODUCTS_BEFORE_HIDING;

  const allInStock = inStockQuantity === totalProductsQuantity;

  const buyButtonElement = createElement('button', {
    className: 'btn btn-primary retailer-item__buy-btn js-transfer-cart',
    type: 'button',
    innerText: inStockQuantity ? _labels.ct_labels_buy_cta_button : _labels.ct_labels_order_cta_button,
    contents: createElement('span', {
        className: 'retailer-item__buy-btn-external-icon icon--direction-x vertical_middle',
        contents: createPesIcon({
          iconName: 'action_new_window',
          size: 'NANO',
          className: 'figure-icon'
        })
      })
  });

  const buyButtonColumn = createRetailerItemColumn(buyButtonElement);

  const retailerDetails = createDetails(retailer, allInStock, inStockQuantity, totalProductsQuantity);

  const retailerRow = createElement('li', {
    className: `retailer-item ${largeProductsQuantity ? 'has-large-quantity' : ''}`,
    dataset: {
      retailer_guid: retailer.RetailerCartId
    },
    contents: [
      imgColumn,
      retailerDetails,
      buyButtonColumn
    ]
  });

  delegateEvents(retailerRow, 'click', {
    // Transfer cart
    '.js-transfer-cart': (e) => {
      onCartTransferButtonClick(retailerRow.dataset.retailer_guid, e.target);
    },
    // Expand retailer products details
    '.js-view-retailer-item-details': () => {
      if (isMobile() || largeProductsQuantity) {
        retailerRow.classList.toggle('is-expanded');
      }
    }
  });

  return retailerRow;
}

/**
 * @param {object} params
 * @param {object} params.channelSightData
 * @param {object} params.unavailableProductsObject
 * @param {object} params.productsInCart
 * @param {CartTransferOptions} params.options
 * @param {CartTransferLabels} params.labels
 * @param {CartTransferMessages} params.messages
 * @param {object} params.callbacks
 * @param {(cartId: string, button: EventTarget) => void} params.callbacks.onCartTransferButtonClick
 */
export function fillRetailerRows(params) {
  // Save to use later
  ({
    productsInCart: _productsInCart,
    unavailableProductsObject: _unavailableProductsObject,
    labels: _labels,
    messages: _messages,
    options: _options
  } = params);

  const { channelSightData, callbacks } = params;

  const retailers = createElement('ul', {
    className: 'retailer-items'
  });

  if (getValueByNs(channelSightData, 'RetailerCarts.length', 0) > 1) {
    const sortedRetailers = channelSightData.RetailerCarts.sort((currentRetailer, nextRetailer) => {
      const currentRetailerInStockQuantity = getValueByNs(currentRetailer, 'ProductsCarriedInStock.length', 0);
      const nextRetailerInStockQuantity = getValueByNs(nextRetailer, 'ProductsCarriedInStock.length', 0);

      return nextRetailerInStockQuantity - currentRetailerInStockQuantity;
    });

    channelSightData.RetailerCarts = sortedRetailers;
  }

  channelSightData.RetailerCarts.reduce((rows, retailer) => {
    const row = renderRetailerRow(retailer, callbacks);

    rows.appendChild(row);
    return rows;
  }, retailers);

  modalInterface.elements.distributorList.appendChild(retailers);
}

/**
 * Create 'Not available products' body element.
 *
 * @param {object[]} notAvailableItems Array containing not available items.
 * @returns {HTMLElement} Not available products body element.
 */
function createNotAvailableBody(notAvailableItems) {
  /** @type {HTMLElement[]} */
  const children = [
    createElement('ul', {
      className: 'retail-not-available__items',
      contents: notAvailableItems.map((item) => createElement('li', {
        className: 'retail-not-available__item',
        contents: createProductItem(item, true)
      }))
    })
  ];

  const classList = ['retail-not-available__body', 'js-retail-not-available'];

  if (notAvailableItems.length > MAX_UNAVAILABLE_ITEMS_TO_SHOW) {
    const viewMoreToggler = createElement('button', {
      type: 'button',
      className: 'retail-not-available__expand pes-mobile-hide js-view-more-unavailable',
      innerText: _labels.ct_labels_expand_not_distributed,
      contents: createElement('span', {
        className: 'retail-not-available__expand-icon icon vertical_middle',
        contents: createPesIcon({
          iconName: 'arrow2_right',
          size: 'NANO',
          className: 'figure-icon'
        })
      })
    });

    children.push(viewMoreToggler);
  } else {
    classList.push('is-expanded');
  }

  if (_options.notDistributedCtaURL) {
    children.push(createElement('a', {
      className: 'btn btn-primary retail-not-available__contact-btn',
      href: _options.notDistributedCtaURL,
      innerText: _labels.ct_labels_not_distributed_cta
    }));
  }

  return createElement('div', {
    classList,
    contents: children
  });
}

/**
 * Create 'Not available products' element.
 *
 * @param {object[]} [unavailableProducts] Array containing not available items.
 */
export function addNotAvailableSection(unavailableProducts) {
  if (!unavailableProducts) {
    return;
  }

  const productsCount = unavailableProducts.length;

  const notAvailableHead = createElement('h3', {
    className: 'retail-not-available__heading',
    innerText: `${_labels.ct_labels_not_distributed_online}${productsCount ? ` (${productsCount})` : ''}`
  });

  const notAvailableSection = createElement('section', {
    className: 'retail-not-available',
    contents: [
      notAvailableHead,
      createNotAvailableBody(unavailableProducts)
    ]
  });

  modalInterface.elements.distributorList.appendChild(notAvailableSection);
}
